import { SignJWT } from 'jose';

// Cache for authentication data
let jwtCache = {
  data: null, // Will store { token, assistantId }
  timestamp: null,
  duration: 50 * 60 * 1000, // 50 minutes
  cacheKey: null,
};

const DYNAMIC_ASSISTANT_KEY = 'vapi_faq_assistant_id'; // New key for the FAQ bot

/**
 * Creates a new, simplified assistant configuration for the FAQ bot.
 * @returns {object} The assistant creation payload.
 */
const createAssistantPayload = () => {
  const systemPrompt = `You're a super chill, friendly helper for this app. Talk like a real person - casual, short, and conversational.

Keep it simple:
- Short answers (1-2 sentences max usually)
- Talk like you're texting a friend
- Use natural speech patterns like "Oh yeah, so..." or "Right, so basically..."
- If you don't know something, just say "Hmm, not sure about that one" or "I don't actually know that"
- Be helpful but keep it brief and natural

You'll get info about the current page to answer questions. Don't make stuff up, and don't offer to do actions for them.

Just be chill and helpful!

(Page context will be added dynamically when conversations start)`;

  return {
    transcriber: {
      provider: 'deepgram',
      model: 'nova-3',
      language: 'en',
    },
    model: {
      provider: 'openai',
      model: 'gpt-4.1-mini',
      messages: [{ role: 'system', content: systemPrompt }],
    },
    voice: {
      provider: 'deepgram',
      model: 'aura-2',
      voiceId: 'andromeda',
    },
    firstMessage: "Hey! What's up? Need help with something?",
  };
};

/**
 * Updates the system prompt of a given assistant.
 * @param {string} privateKey - Vapi private key for authorization.
 * @param {string} assistantId - The ID of the assistant to update.
 * @param {string} newSystemPrompt - The new system prompt content.
 * @returns {Promise<boolean>} True if successful, false otherwise.
 */
export const updateAssistant = async (privateKey, assistantId, newSystemPrompt) => {
  console.log(`Updating assistant ${assistantId} with new context...`);
  try {
    const response = await fetch(`https://api.vapi.ai/assistant/${assistantId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${privateKey}`,
      },
      body: JSON.stringify({
        model: {
          provider: 'openai',
          model: 'gpt-4.1-mini',
          messages: [{ role: 'system', content: newSystemPrompt }],
        },
      }),
    });

    if (!response.ok) {
      const errorBody = await response.json();
      console.error('Failed to update assistant:', errorBody);
      throw new Error(`API Error: ${response.status}`);
    }

    console.log('Assistant updated successfully.');
    return true;

  } catch (error) {
    console.error('Error in updateAssistant:', error);
    return false;
  }
};

/**
 * Checks for a cached assistant ID or creates a new one.
 * @param {string} privateKey - Your Vapi private key.
 * @returns {Promise<string|null>} The assistant ID.
 */
const getOrCreateDynamicAssistant = async (privateKey) => {
  const cachedId = sessionStorage.getItem(DYNAMIC_ASSISTANT_KEY);
  if (cachedId) {
    console.log(`Using cached FAQ assistant: ${cachedId}`);
    return cachedId;
  }

  console.log('Creating a new FAQ assistant...');
  const assistantPayload = createAssistantPayload();

  try {
    const response = await fetch('https://api.vapi.ai/assistant', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${privateKey}`,
      },
      body: JSON.stringify(assistantPayload),
    });

    if (!response.ok) {
      const errorBody = await response.json();
      console.error('Failed to create dynamic assistant:', errorBody);
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const newAssistant = await response.json();
    sessionStorage.setItem(DYNAMIC_ASSISTANT_KEY, newAssistant.id);
    console.log(`Successfully created FAQ assistant: ${newAssistant.id}`);
    return newAssistant.id;

  } catch (error) {
    console.error('Error in getOrCreateDynamicAssistant:', error);
    return null;
  }
};

/**
 * Generate a JWT token for the current environment
 * @returns {Promise<{token: string, assistantId: string}|null>} JWT token and assistant ID, or null if missing credentials
 */
export const generateCurrentVapiJWT = async () => {
  const orgId = import.meta.env.VITE_VAPI_ORG_ID;
  const privateKey = import.meta.env.VITE_VAPI_PRIVATE_KEY;

  // Debug logging to verify keys are loaded
  console.log('🔑 Vapi Key Debug:', {
    orgIdLoaded: !!orgId,
    privateKeyLoaded: !!privateKey,
    orgIdLength: orgId ? orgId.length : 0,
    privateKeyLength: privateKey ? privateKey.length : 0,
    currentOrigin: window.location.origin
  });

  if (!orgId || !privateKey) {
    console.error('❌ Missing Vapi credentials - check your environment variables');
    return null;
  }

  const assistantId = await getOrCreateDynamicAssistant(privateKey);
  if (!assistantId) {
    console.error('Failed to get or create a dynamic assistant.');
    return null;
  }

  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;

  const fullOrigin = window.location.origin;

  const calculatedOrigin = hostname === 'localhost'
    ? `${protocol}//${hostname}:${port}`
    : `${protocol}//${hostname}`;

  console.log('🌐 Origin Detection Debug:', {
    protocol,
    hostname,
    port,
    fullOrigin,
    calculatedOrigin,
    areEqual: fullOrigin === calculatedOrigin,
    href: window.location.href
  });

  const origin = fullOrigin;

  const cacheKey = `${orgId}-${assistantId}-${origin}`;
  const now = Date.now();

  if (jwtCache.data && 
      jwtCache.cacheKey === cacheKey &&
      (now - jwtCache.timestamp) < jwtCache.duration) {
    console.log('Using cached JWT data.');
    return jwtCache.data;
  }

  console.log('Generating new JWT for FAQ assistant...');

  try {
    const token = await generateVapiJWT(orgId, privateKey, assistantId, origin);
    const authData = { token, assistantId, privateKey };

    jwtCache = {
      data: authData,
      timestamp: now,
      cacheKey,
      duration: jwtCache.duration
    };

    return authData;
  } catch (error) {
    console.error('Failed to generate Vapi JWT with restrictions:', error);

    // Try fallback without restrictions
    console.log('🔄 Trying fallback JWT without restrictions...');
    try {
      const fallbackToken = await generateVapiJWT(orgId, privateKey, assistantId, origin, false);
      const fallbackAuthData = { token: fallbackToken, assistantId, privateKey };

      console.log('✅ Fallback JWT generated successfully');
      return fallbackAuthData;
    } catch (fallbackError) {
      console.error('Failed to generate fallback Vapi JWT:', fallbackError);
      return null;
    }
  }
};

/**
 * Generate a public scoped JWT token for Vapi Web SDK
 * @returns {Promise<string>} JWT token
 */
export const generateVapiJWT = async (orgId, privateKey, assistantId, origin, useRestrictions = true) => {
  const payload = {
    orgId: orgId,
    token: {
      tag: "public",
      ...(useRestrictions && {
        restrictions: {
          enabled: true,
          allowedOrigins: [origin],
          // Removed allowedAssistantIds when allowTransientAssistant is true to enable dynamic assistant loading
          // allowedAssistantIds: [assistantId],
          allowTransientAssistant: true,
        }
      })
    }
  };

  // Debug logging for JWT generation
  console.log('🔧 JWT Debug Info:', {
    origin: origin,
    assistantId: assistantId,
    orgId: orgId,
    useRestrictions,
    payload: JSON.stringify(payload, null, 2)
  });

  const secret = new TextEncoder().encode(privateKey);
  const jwt = await new SignJWT(payload)
    .setProtectedHeader({ alg: 'HS256' })
    .setExpirationTime('1h')
    .sign(secret);

  console.log('✅ Generated JWT token (first 50 chars):', jwt.substring(0, 50) + '...');
  return jwt;
}; 